import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';
import { ContextStore, ExperimentStore, QueryObject, SerializedData, Store } from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import * as cookies from 'browser-cookies';
import { computed, observable } from 'mobx';
import { LandingPageType } from '../utils/geoLocation';
import {
  ActivityDestinationInput,
  ActivityDestinationViewSearch,
  ActivityMarketingInfoInput,
  ActivitySearchFiltersInput,
  ActivitySearchViewOptions,
  ActivitySelectedValueInput,
  ActivitySort,
  ContextInput,
  PaginationInput
} from '../common/__generated__/api/types';
import { equals, getFilteredObjectKeys } from '../helpers/common-helper';
import { activityDateRangeInput } from '../helpers/controller-date-helper';
import { getIsoFormatDateFromDateString } from '../helpers/date-helper';
import { MarketingInfo } from '../helpers/marketing-cookie-helper';

/* eslint-disable @typescript-eslint/no-var-requires */
const xss = require('xss');
const DEFAULT_ACTIVITIES_LENGTH = 50;
const NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 328;
const PAGINATION_START_INDEX = 0;
const MCTC_MOD_ENABLED = 2;
const GROUND_TRANSFER_BANNER_COOKIE = 'ground_transfer_banner_shown';
export const FILTER_URL_PARAM_PREFIX = 'filter.';

// These values should only be set in controllers
// Coming from the query
export interface SearchQueryObject extends QueryObject {
  readonly location: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly sort: string;
  readonly swp: string;
  readonly slimSearchKeyword: string;
  readonly selectedId: string;
  readonly rid: string;
  readonly mctc: string;
  readonly travelDurationFromHotelEnabled: string;
  readonly regionType: string;
  readonly latLong: string;
  readonly landingPage: string;
}

export class SearchCriteriaStore extends Store {
  @observable public isQueryStringUpdated = true;
  @observable public searchQuery: SearchQueryObject = {} as SearchQueryObject;
  public firstLoadActivityCount?: number;
  public localDateFormat?: string;
  public numOfDaysBookableInAdvance: number = NUM_OF_DAYS_BOOKABLE_IN_ADVANCE;
  public scrollPosition: number;
  public activityToFocus: {
    index: number | null;
    element: React.RefObject<any> | null;
  } = {
    index: null,
    element: null
  };

  private context?: ContextInput;
  private defaultActivitiesLength: number = DEFAULT_ACTIVITIES_LENGTH; // Should only be set in the controller
  private marketingInfo?: MarketingInfo;
  private paginationIndex: number = PAGINATION_START_INDEX;
  private experimentStore: ExperimentStore | null = null;

  public constructor(
    state: SerializedData = {},
    logger: Logger | ClientLogger = NOOP_LOGGER,
    appConfig: KeyedObject,
    marketingInfo?: MarketingInfo
  ) {
    super(state, logger);
    if (appConfig) {
      this.firstLoadActivityCount = appConfig.firstLoadActivityCount;
    }
    this.scrollPosition = 0;
    this.activityToFocus.element = null;
    this.activityToFocus.index = null;

    if (marketingInfo) {
      this.marketingInfo = marketingInfo;
    }
  }

  public setContext(context: ContextStore): void {
    this.context = getBexApiContext(context);
  }

  @computed
  public get searchVariables(): ActivityDestinationViewSearch.Variables | null {
    if (this.context && this.localDateFormat) {
      return {
        context: this.context,
        destination: this.activityDestinationInput,
        dateRange: activityDateRangeInput(this.searchQuery, this.localDateFormat),
        filters: this.activitySearchFiltersInput,
        sort: this.activitySort,
        pagination: this.pagination,
        marketing: this.marketing,
        searchViewOptions: this.searchViewOptions
      };
    }

    return null;
  }

  public get getPaginationIndex(): number {
    return this.paginationIndex;
  }

  public hydrate(data: SerializedData): void {
    this.searchQuery = { ...data.searchQuery };
    this.context = { ...data.context };
    this.isQueryStringUpdated = data.queryStringUpdated;
    this.localDateFormat = data.localDateFormat;
    this.firstLoadActivityCount = data.firstLoadActivityCount;
    this.scrollPosition = 0;
    this.activityToFocus.index = null;
    this.activityToFocus.element = null;

    this.marketingInfo = data.marketingInfo;
    delete data.marketingInfo;
  }

  private getFilterSpecificKeysFromQueryObjects(queries: QueryObject[]): string[] {
    let combinedQueryObj = {};
    queries.forEach(query => {
      combinedQueryObj = { ...combinedQueryObj, ...query };
    });

    return getFilteredObjectKeys(combinedQueryObj, FILTER_URL_PARAM_PREFIX);
  }

  public isQueryKeysDifferent(query: QueryObject): boolean {
    const searchKeys = [
      'location',
      'endDate',
      'startDate',
      'sort',
      'swp',
      'slimSearchKeyword',
      'rid',
      'selectedId',
      'mctc',
      'travelDurationFromHotelEnabled',
      'regionType',
      'latLong',
      'landingPage',
      ...this.getFilterSpecificKeysFromQueryObjects([query, this.searchQuery])
    ];

    return searchKeys.some(key => {
      if (this.localDateFormat && key && this.searchQuery[key] && (key === 'startDate' || key === 'endDate')) {
        return !equals(
          getIsoFormatDateFromDateString(query[key], this.localDateFormat),
          getIsoFormatDateFromDateString(this.searchQuery[key] || '', this.localDateFormat)
        );
      }

      return !equals(query[key], this.searchQuery[key]);
    });
  }

  public hasLocation(query: QueryObject): boolean {
    return query.location !== undefined && query.location !== '';
  }

  public resetPaginationIndex(): void {
    this.paginationIndex = 0;
  }

  public showMore(): void {
    this.paginationIndex += 1;
  }

  public resetSearchCriteriaStore(): void {
    this.resetPaginationIndex();
    this.searchQuery = {} as SearchQueryObject;
    this.isQueryStringUpdated = true;
    this.scrollPosition = 0;
    this.activityToFocus.index = null;
    this.activityToFocus.element = null;
  }

  public populateDefaultSortIfApplicable(): void {
    if (this.searchQuery && !this.searchQuery.sort) {
      this.searchQuery = {
        ...this.searchQuery,
        sort: ActivitySort.RECOMMENDED.valueOf()
      };
    }
  }

  public populateRegionTypeForLandingPage(query: KeyedObject): void {
    if (query && query.landingPage) {
      query.regionType = query.landingPage;
      if (this.searchQuery) {
        this.searchQuery = {
          ...this.searchQuery,
          regionType: query.regionType
        };
      }
    }
  }

  public setExperimentStore(experimentStore: ExperimentStore): void {
    this.experimentStore = experimentStore;
  }

  public getExperimentStore(): ExperimentStore | null {
    return this.experimentStore;
  }

  public setGroundTransfersBannerCookie(): void {
    if (typeof document !== 'undefined' && document.cookie) {
      const isCookieSet = cookies.get(GROUND_TRANSFER_BANNER_COOKIE);
      if (!isCookieSet) {
        cookies.set(GROUND_TRANSFER_BANNER_COOKIE, 'true');
      }
    }
  }

  private get marketing(): ActivityMarketingInfoInput {
    const marketingInfo: ActivityMarketingInfoInput = {};

    if (this.marketingInfo) {
      marketingInfo.sem = this.marketingInfo.sem;
      marketingInfo.semdtl = this.marketingInfo.semdtl;
    }

    if (this.lxModEnabled) {
      marketingInfo.mctc = MCTC_MOD_ENABLED;
    }

    return marketingInfo;
  }

  private get lxModEnabled(): boolean {
    const mctcIsSet = this.searchQuery.mctc ? this.searchQuery.mctc === MCTC_MOD_ENABLED.toString() : false;

    let cookieIsSet = false;
    if (process.env.BROWSER) {
      cookieIsSet = document.cookie.includes('lx_mod_enabled=true');
    }

    return mctcIsSet || cookieIsSet;
  }

  private get pagination(): PaginationInput {
    const pagination: PaginationInput = {};
    pagination.size = this.defaultActivitiesLength;
    pagination.startingIndex = this.paginationIndex;

    return pagination;
  }

  private get activityDestinationInput(): ActivityDestinationInput {
    const latLongArray = this.searchQuery.latLong ? this.searchQuery.latLong.split(',') : null;
    const latLong =
      latLongArray && latLongArray.length > 1
        ? { latitude: parseFloat(latLongArray[0]), longitude: parseFloat(latLongArray[1]) }
        : null;

    return {
      regionName: this.sanitizeInput(this.searchQuery.location),
      pinnedActivityId: this.searchQuery.selectedId,
      regionId: this.searchQuery.rid,
      regionType: this.searchQuery.regionType,
      coordinates: latLong
    };
  }

  private get activitySearchFiltersInput(): ActivitySearchFiltersInput | null {
    if (!this.searchQuery) {
      return null;
    }
    const selections = this.getSearchCriteriaSelection();

    return { selections };
  }

  private get activitySort(): ActivitySort | null {
    return this.searchQuery.sort ? (ActivitySort as any)[this.searchQuery.sort] : null;
  }

  private getSearchCriteriaSelection(): ActivitySelectedValueInput[] {
    const selections: ActivitySelectedValueInput[] = [];
    const filtersKeys = getFilteredObjectKeys(this.searchQuery, FILTER_URL_PARAM_PREFIX);
    const keyword = this.sanitizeInput(this.searchQuery.slimSearchKeyword);

    selections.push({
      id: 'sort',
      value: this.searchQuery.sort ? this.searchQuery.sort : ''
    });

    if (keyword && keyword.length > 0) {
      selections.push({
        id: 'slimSearchKeyword',
        value: keyword
      });
    }

    filtersKeys.forEach(filtersKey => {
      const filterId = filtersKey.split('.')[1];
      if (this.searchQuery[filtersKey]) {
        const filterVals = this.searchQuery[filtersKey].split(',');
        filterVals.forEach(filterVal => {
          if (filterVal && filterVal !== '') {
            selections.push({
              id: filterId,
              value: filterVal
            });
          }
        });
      }
    });

    return selections;
  }

  private get showGroundTransfersBanner(): boolean {
    if (typeof document !== 'undefined' && document.cookie) {
      return !cookies.get(GROUND_TRANSFER_BANNER_COOKIE);
    }

    return true;
  }

  private get searchViewOptions(): ActivitySearchViewOptions[] | null {
    const searchViewOptions: ActivitySearchViewOptions[] = [];

    if (this.searchQuery.travelDurationFromHotelEnabled === 'true') {
      searchViewOptions.push(ActivitySearchViewOptions.TRAVEL_DISTANCE_FROM_HOTEL);
    }

    // Use off as default is on. Open to suggestion here.
    if (this.searchQuery.swp !== 'off') {
      searchViewOptions.push(ActivitySearchViewOptions.SHOP_WITH_POINTS);
    }

    if (
      this.searchQuery.regionType === LandingPageType.LOCAL ||
      this.searchQuery.regionType === LandingPageType.ULTRA_LOCAL
    ) {
      searchViewOptions.push(ActivitySearchViewOptions.NEARBY_ACTIVITIES);
    }

    if (this.showGroundTransfersBanner) {
      searchViewOptions.push(ActivitySearchViewOptions.SHOW_GT_BANNER);
    }

    return searchViewOptions.length > 0 ? searchViewOptions : null;
  }

  public isDateOrLocationChanged(query: KeyedObject): boolean {
    const searchKeys = ['location', 'endDate', 'startDate'];

    return searchKeys.some(key => {
      if ((key === 'startDate' || key === 'endDate') && this.localDateFormat && this.searchQuery[key]) {
        return !equals(
          getIsoFormatDateFromDateString(query[key], this.localDateFormat),
          getIsoFormatDateFromDateString(this.searchQuery[key], this.localDateFormat)
        );
      }

      return this.searchQuery[key] && !equals(query[key], this.searchQuery[key]);
    });
  }

  public isSortOrFilterChanged(query: KeyedObject): boolean {
    return (
      !equals(this.searchQuery.sort, query.sort) ||
      !equals(this.searchQuery['filter.categories'], query['filter.categories'])
    );
  }

  public resetQueryObject(query: KeyedObject): void {
    const searchResetKeys = [
      'sort',
      'slimSearchKeyword',
      'selectedId',
      'rid',
      'regionType',
      'srpOpen',
      ...this.getFilterSpecificKeysFromQueryObjects([query])
    ];
    searchResetKeys.forEach(key => {
      query[key] = '';
    });

    if (this.searchQuery.location && !equals(this.searchQuery.location, query.location)) {
      delete query.latLong;
      delete query.travelDurationFromHotelEnabled;
    }
  }

  private sanitizeInput(input: string): string {
    const xssOptions = {
      stripIgnoreTag: true,
      whiteList: {}
    };

    return xss(input, xssOptions);
  }
}
